import { openableProducts } from '@/lib/productNav'
import type { Role, User } from '@/types'

/**
 * Post-login landing per role (UI-POLISH-2 / Tier 1 ITEM 1).
 *
 * `tester` is a sandbox persona normalised to `cpa` for authz in
 * core/db/auth_service.py, so it lands where a CPA lands. `analyst` also lands
 * on /dashboard, but Dashboard filters the pipeline to that analyst's own deals
 * ("My Work") — see Dashboard.tsx.
 */
const LANDING_BY_ROLE: Record<Role, string> = {
  super_admin: '/dashboard',
  cpa: '/dashboard',
  tester: '/dashboard',
  analyst: '/dashboard',
  firm: '/dashboard',
  // Valuation entitlements carry no QoE grant — /dashboard is a page they are
  // not entitled to and have no nav out of.
  valuation_signer: '/valuation',
  valuation_owner: '/valuation',
  pending_approval: '/dashboard',
}

/** Unauthenticated / terminal routes that must never be a post-login target. */
const NON_RETURNABLE = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/request-access',
  '/seller',
  '/buyer',
])

export function landingPathForRole(role?: Role | null): string {
  return (role && LANDING_BY_ROLE[role]) || '/dashboard'
}

/**
 * Where an authenticated user belongs, entitlement first (RBAC-1 build item 5).
 *
 * Order: no entitlement payload -> the role map, unchanged; exactly one
 * openable product -> straight into it, because making a single-product user
 * pick from a list of one is a hop that buys nothing; otherwise -> /hub, which
 * is also where zero lands: with nothing openable, /dashboard is a page that
 * would 403 and has no nav out of it, while the hub can say so honestly and
 * still show what the firm could buy.
 *
 * This subsumes the old VALUATION_ONLY_ROLES special case. A valuation-only
 * account resolves to exactly one openable product and lands on /valuation
 * either way — via entitlements when the payload is present, via LANDING_BY_ROLE
 * when it is not.
 */
export function landingPathForUser(
  user: Pick<User, 'role' | 'products'> | null | undefined,
): string {
  if (!user?.products) return landingPathForRole(user?.role)
  const openable = openableProducts(user.products)
  if (openable.length === 1) return openable[0].href
  return '/hub'
}

/**
 * True when `pathname` is a protected deep link worth returning to after login.
 * A bare '/' is not: it only re-enters DefaultRedirect, which would bounce the
 * user through an extra hop instead of landing them on their role's home.
 */
export function isReturnablePath(pathname?: string | null): boolean {
  if (!pathname || pathname === '/') return false
  return !NON_RETURNABLE.has(pathname)
}

/**
 * Where to send a user immediately after authentication.
 *
 * Order: forced password change > preserved deep link > role landing. The deep
 * link keeps its query string and hash so `?tab=…` style links survive login.
 */
export function postLoginPath(
  user: Pick<User, 'role' | 'must_change_password' | 'products'> | null | undefined,
  from?: { pathname?: string; search?: string; hash?: string } | null,
): string {
  if (user?.must_change_password) return '/getting-started'
  if (isReturnablePath(from?.pathname)) {
    return `${from!.pathname}${from!.search ?? ''}${from!.hash ?? ''}`
  }
  return landingPathForUser(user)
}
